import React, { useCallback, useRef, useState, useEffect } from 'react'
import { InView } from 'react-intersection-observer'
import { logger } from '@/utils/logging'

interface FeaturedProjectVideoBackgroundProps {
  backgroundVideo: string
  poster?: string
}

export const FeaturedProjectVideoBackground: React.FC<FeaturedProjectVideoBackgroundProps> = ({
  backgroundVideo,
  poster,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [shouldPlay, setShouldPlay] = useState(false)
  const playPromiseRef = useRef<Promise<void> | null>(null)

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true)
    if (shouldPlay && videoRef.current) {
      playPromiseRef.current = videoRef.current.play().catch((error) => {
        if (error.name !== 'AbortError') {
          logger().warn('Failed to auto-play background video', { error, backgroundVideo })
        }
      })
    }
  }, [shouldPlay, backgroundVideo])

  const handleError = useCallback(() => {
    setHasError(true)
    logger().warn('Failed to load background video', { backgroundVideo })
  }, [backgroundVideo])

  const handleInView = useCallback(
    (inView: boolean) => {
      setShouldPlay(inView)

      if (!videoRef.current) return

      if (inView && isLoaded) {
        playPromiseRef.current = videoRef.current.play().catch((error) => {
          if (error.name !== 'AbortError') {
            logger().warn('Failed to auto-play background video', { error, backgroundVideo })
          }
        })
      } else if (!inView) {
        if (playPromiseRef.current) {
          playPromiseRef.current
            .then(() => {
              if (videoRef.current) {
                videoRef.current.pause()
              }
            })
            .catch(() => {
              if (videoRef.current) {
                videoRef.current.pause()
              }
            })
            .finally(() => {
              playPromiseRef.current = null
            })
        } else if (videoRef.current) {
          videoRef.current.pause()
        }
      }
    },
    [isLoaded, backgroundVideo],
  )

  useEffect(() => {
    setIsLoaded(false)
    setHasError(false)
    playPromiseRef.current = null
  }, [backgroundVideo])

  if (hasError) {
    return (
      <div
        className="absolute left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover bg-center"
        style={{ backgroundImage: poster ? `url(${poster})` : undefined }}
      />
    )
  }

  return (
    <InView threshold={0.1} rootMargin="50px" onChange={handleInView} triggerOnce={false}>
      {({ ref }) => (
        <div ref={ref} className="absolute inset-0">
          <div className="absolute inset-0 z-10 -mb-px bg-gradient-to-t from-core-gray-950" />
          <video
            ref={videoRef}
            className="absolute left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover"
            playsInline
            autoPlay
            muted
            loop
            preload="auto"
            poster={poster}
            onLoadedData={handleLoadedData}
            onError={handleError}
            src={backgroundVideo}
            style={{
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
          {!isLoaded && poster && (
            <div
              className="absolute left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover bg-center"
              style={{ backgroundImage: `url(${poster})` }}
            />
          )}
        </div>
      )}
    </InView>
  )
}
